import { useEffect, useState, useMemo, useRef } from 'react';

import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    AspectRatio,
    Box,
    Button,
    Card,
    Divider,
    Flex,
    FormControl,
    Heading,
    Icon,
    Image,
    Link,
    ListItem,
    Popover,
    PopoverAnchor,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Select,
    SlideFade,
    Spacer,
    Text,
    UnorderedList,
    useBreakpointValue,
    Tag,
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    FormLabel
} from '@chakra-ui/react';

import { DataTable } from './Misc/DataTable';
import { SongDataItem } from './Misc/SongDataItem';

export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id }
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <Select
      size={"xs"}
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      variant={"outline"}
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </Select>
  );
}

export function Ranking({eventConfig, getRanking}) {

    const isMobile = useBreakpointValue(
        {
          "base": ()=> true,
          "xl": ()=>  false
        }
    );     

    const refreshTime = 180000;

    const [rankingTableSort, setRankingTableSort] = useState([]);

    const [chartData, setChartData] = useState(false);
    const [rankingData, setRankingData] = useState(false);
    const [rankingFilter, setRankingFilter] = useState({});
    
    const [rankingDivision, setRankingDivision] = useState("");
    const [rankingGame, setRankingGame] = useState("");
    const [rankingType, setRankingType] = useState("");

    const rankingDataRequest = async () => {
        const newRankingData = await getRanking();

        console.log(newRankingData);

        const chartData = newRankingData?.chartData
            ? newRankingData.chartData
            : [];

        const rankingData = newRankingData?.rankingData
            ? newRankingData.rankingData
            : [];

        setChartData([...chartData]);
        setRankingData([...rankingData]);
    };

    useEffect(()=>{
        let newRankingTableSort = [{ 
                id: 'TEAM_SJ_TOTAL_POINTS',
                desc: true
        }];

        let newRankingFilter = updateRankingFilter("teams_division", rankingDivision, false);
        newRankingFilter = updateRankingFilter("teams_game", rankingGame, false, newRankingFilter);
        newRankingFilter = updateRankingFilter("teams_type", rankingType, false, newRankingFilter);

        setRankingTableSort(newRankingTableSort);
        setRankingFilter(newRankingFilter);
    },[rankingDivision, rankingGame, rankingType]);

    const updateRankingFilter = (id=false, value=false, doSet=true, passedRankingFilter=false) => {
        const newRankingFilter = passedRankingFilter ? {...passedRankingFilter} : {...rankingFilter};

        if (value === "") {
            delete newRankingFilter[id];
        } else {
            newRankingFilter[id] = value;
        }

        if (doSet) {
            setRankingFilter(newRankingFilter);
        } else {
            return newRankingFilter;
        }
    };

    const initialState = useMemo(()=>{
        return { 
            pageIndex: 0, 
            pageSize: 9999,
            sortBy: [
                {
                    id: 'registration_best_total',
                    desc: true
                }
            ]
        }
    }, []);

    const columns = useMemo(() => 
        [
            {
                Header: isMobile() ? "#" : "Rank",
                accessor: "",
                Cell: (row) => {
                    const rowIds = [];
                    row.sortedRows.forEach((sortedRow)=>{
                        rowIds.push(sortedRow.index);
                    });
                    
                    const rank = rowIds.indexOf(row.row.index) + 1;
                    return <div>{rank}</div>;
                },
                disableSortBy: true,
                disableFilters: true,
                align: "center",
                width: 40,
            },
            {
                Header: "Team",
                accessor: `teams_name`,          
                Filter: "",
                filter: "",
                align: "center",
                width: 125,
                disableSortBy: true,
            },    
            {
                Header: "Division",
                accessor: `teams_division`,
                Cell: (row) => {
                    const teams_division = row?.cell?.value;
                    
                    return <span style={{textTransform: "capitalize"}}>{teams_division}</span>;
                },                      
                Filter: "",
                filter: "equals",
                align: "center",
                width: 85,
                disableSortBy: true,
                show: !isMobile()
            },               
            {
                Header: "Game",
                accessor: `teams_game`,
                Cell: (row) => {
                    const teams_game = row?.cell?.value;

                    const transformVal = teams_game === "itg"
                        ? "uppercase"
                        : "capitalize";
                    
                    return <span style={{textTransform: transformVal}}>{teams_game}</span>;
                },                  
                Filter: "",
                filter: "equals",
                align: "center",
                width: 60,
                disableSortBy: true,
                show: !isMobile()
            },          
            {
                Header: "Type",
                accessor: `teams_type`,
                Cell: (row) => {
                    let teams_type = row?.cell?.value;

                    teams_type = teams_type === "double"
                        ? "dbl"
                        : "sgl";
                    
                    return <span style={{textTransform: "capitalize"}}>{teams_type}</span>;
                },                       
                Filter: "",
                filter: "equals",
                align: "center",
                width: 45,
                disableSortBy: true,
                show: !isMobile()
            },                       
            {
                Header: "ECS Pts",                
                accessor: "TEAM_ECS_POINTS",
                Filter: "",
                filter: "",
                align: "center",
                width: 85,
                disableSortBy: true,
                show: !isMobile()
            },        
            
            {
                Header: "Trivia Pts",
                accessor: "team_trivia_points",
                Filter: "",
                filter: "",
                width: 85,
                disableSortBy: true,
                show: !isMobile()
            },        
            {
                Header: "Sticker Pts",
                accessor: "team_sticker_points",
                Filter: "",
                filter: "",
                width: 85,
                disableSortBy: true,
                show: !isMobile()
            },       
            {
                Header: "Speedrun Pts",
                accessor: "team_speedrun_points",
                Filter: "",
                filter: "",
                width: 85,
                disableSortBy: true,
                show: !isMobile()
            },                
            {
                Header: "Archi Pts",
                accessor: "team_archi_points",
                Filter: "",
                filter: "",
                width: 85,
                disableSortBy: true,
                show: !isMobile()
            },   
            {
                Header: "Xiwangz Pts",
                accessor: "team_xiwangz_points",
                Filter: "",
                filter: "",
                width: 85,
                disableSortBy: true,
                show: !isMobile()
            },   
            {
                Header: "Total",                
                accessor: "TEAM_SJ_TOTAL_POINTS",
                Filter: "",
                filter: "between",
                sortType: "number",
                width: 85,
                disableSortBy: true,                
            },     
/*                       
            {
                Header: "Gender",
                accessor: `members_sex`,
                Filter: "",
                filter: "equals",
                width: 60,
                disableSortBy: true,
                show: !isMobile()
            },
            {
                Header: isMobile() ? "TPX" : "ITG Single TPX",
                accessor: "registration_itg_singles_tpx",
                Filter: "",
                filter: "",
                width: 65,
                disableSortBy: true,                
                show:    
                    hasItgSingle && rankingGame === "itg" && rankingType === "single"              
            },        
            {
                Header: isMobile() ? "LPX" : "ITG Single LPX",
                accessor: "registration_itg_singles_lpx",
                Filter: "",
                filter: "",
                sortType: "number",
                width: 65,
                disableSortBy: true,                
                show:
                    hasItgSingle && rankingGame === "itg" && rankingType === "single"
            },                
            {
                Header: isMobile()
                        ? "Total" 
                        : rankingGame === ""
                            ? "ITG Single"
                            : "ITG Single Total",                
                accessor: "registration_itg_singles_total",
                Filter: "",
                filter: "between",
                sortType: "number",
                width: 85,
                disableSortBy: true,                
                show:
                    hasItgSingle && ((rankingGame === "itg" && (rankingType === "single" || (!isMobile() && rankingType === ""))) || (!isMobile() && rankingGame === "" && (rankingType === "single" || rankingType === "")))
            },
            {
                Header: isMobile() ? "TPX" : "ITG Double TPX",
                accessor: "registration_itg_doubles_tpx",
                Filter: "",
                filter: "",
                width: 65,
                disableSortBy: true,                
                show:    
                    hasItgDouble && rankingGame === "itg" && rankingType === "double"               
            },
            {
                Header: isMobile() ? "LPX" : "ITG Double LPX",
                accessor: "registration_itg_doubles_lpx",
                Filter: "",
                filter: "",
                sortType: "number",
                width: 65,
                disableSortBy: true,                
                show:
                    hasItgDouble && rankingGame === "itg" && rankingType === "double"
            },
            {
                Header: 
                    isMobile() 
                        ? "Total" 
                        : rankingGame === ""
                            ? "ITG Double"
                            : "ITG Double Total",
                accessor: "registration_itg_doubles_total",
                Filter: "",
                filter: "between",
                sortType: "number",
                width: 85,
                disableSortBy: true,                
                show:
                    hasItgDouble && ((rankingGame === "itg" && (rankingType === "double" || (!isMobile() && rankingType === ""))) || (!isMobile() && rankingGame === "" && (rankingType === "double" || rankingType === "")))
            },        
            {
                Header: isMobile() ? "TPX" : "Pump Single TPX",
                accessor: "registration_pump_singles_tpx",
                Filter: "",
                filter: "",
                sortType: "number",
                width: 65,
                disableSortBy: true,                
                show:    
                    hasPumpSingle && rankingGame === "pump" && rankingType === "single"              
            },        
            {
                Header: isMobile() ? "LPX" : "Pump Single LPX",
                accessor: "registration_pump_singles_lpx",
                Filter: "",
                filter: "",
                sortType: "number",
                width: 65,
                disableSortBy: true,                
                show:
                    hasPumpSingle && rankingGame === "pump" && rankingType === "single"
            },                
            {
                Header: isMobile()
                        ? "Total" 
                        : rankingGame === ""
                            ? "Pump Single"
                            : "Pump Single Total",                 
                accessor: "registration_pump_singles_total",
                Filter: "",
                filter: "between",
                sortType: "number",
                width: 85,
                disableSortBy: true,                
                show:
                    hasPumpSingle && ((rankingGame === "pump" && (rankingType === "single" || (!isMobile() && rankingType === ""))) || (!isMobile() && rankingGame === "" && (rankingType === "single" || rankingType === "")))
            },
            {
                Header: isMobile() ? "TPX" : "Pump Double TPX",
                accessor: "registration_pump_doubles_tpx",
                Filter: "",
                filter: "",
                width: 65,
                disableSortBy: true,                
                show:    
                    hasPumpDouble && rankingGame === "pump" && rankingType === "double"             
            },
            {
                Header: isMobile() ? "LPX" : "Pump Double LPX",
                accessor: "registration_pump_doubles_lpx",
                Filter: "",
                filter: "",
                sortType: "number",
                width: 65,
                disableSortBy: true,                
                show:
                    hasPumpDouble && rankingGame === "pump" && rankingType === "double"
            },                
            {
                Header: 
                    isMobile() 
                        ? "Total" 
                        : rankingGame === ""
                            ? "Pump Double"
                            : "Pump Double Total",                
                accessor: "registration_pump_doubles_total",
                Filter: "",
                filter: "between",
                sortType: "number",
                width: 85,
                disableSortBy: true,                
                show:
                    hasPumpDouble && ((rankingGame === "pump" && (rankingType === "double" || (!isMobile() && rankingType === ""))) || (!isMobile() && rankingGame === "" && (rankingType === "double" || rankingType === "")))
            },
            {
                Header: isMobile() ? "All" : "Combined",
                accessor: "registration_itg_points",
                Filter: "",
                filter: "",
                sortType: "number",
                width: 85,
                disableSortBy: true,                
                show:
                    rankingGame === "itg" && rankingType === ""
            },             
            {
                Header: isMobile() ? "All" : "Combined",
                accessor: "registration_pump_points",
                Filter: "",
                filter: "",
                sortType: "number",
                width: 85,
                disableSortBy: true,                
                show:
                    rankingGame === "pump" && rankingType === ""
            },     
            {
                Header: isMobile() ? "All" : "Combined",
                accessor: "registration_singles_points",
                Filter: "",
                filter: "",
                sortType: "number",
                width: 85,
                disableSortBy: true,                
                show:
                    rankingGame === "" && rankingType === "single"
            },               
            {
                Header: isMobile() ? "All" : "Combined",
                accessor: "registration_doubles_points",
                Filter: "",
                filter: "",
                sortType: "number",
                width: 85,
                disableSortBy: true,                
                show:
                    rankingGame === "" && rankingType === "double"
            },                                        
            {
                Header: isMobile() ? "All" : "Combined",
                accessor: "registration_overall_points",
                Filter: "",
                filter: "",
                // this isn't sortable so we can't actually use it, but maybe good example for some other time
                Cell: (row) => {
                    const rowCells = row?.row?.allCells;
                    
                    const itgSingleTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_itg_singles_total"))[0]?.value);
                    const itgDoubleTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_itg_doubles_total"))[0]?.value);
                    const pumpSingleTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_pump_singles_total"))[0]?.value);
                    const pumpDoubleTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_pump_doubles_total"))[0]?.value);

                    const dbCombinedTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_overall_points"))[0]?.value);

                    let combinedTotal = 0;

                    if (rankingGame === "") {
                        if (rankingType === "") {
                            //combinedTotal = itgSingleTotal + itgDoubleTotal + pumpSingleTotal + pumpDoubleTotal;
                            combinedTotal = dbCombinedTotal;
                        } else if (rankingType === "single") {
                            combinedTotal = itgSingleTotal + pumpSingleTotal;
                        } else if (rankingType === "double") {
                            combinedTotal = itgDoubleTotal + pumpDoubleTotal;
                        }
                    } else if (rankingGame === "itg") {
                        if (rankingType === "") {
                            combinedTotal = itgSingleTotal + itgDoubleTotal;
                        } else if (rankingType === "single") {
                            //shouldn't be relevant
                            combinedTotal = itgSingleTotal;
                        } else if (rankingType === "double") {
                            //shouldn't be relevant
                            combinedTotal = itgDoubleTotal;
                        }
                    } else if (rankingGame === "pump") {
                        if (rankingType === "") {
                            combinedTotal = pumpSingleTotal + pumpDoubleTotal;
                        } else if (rankingType === "single") {
                            //shouldn't be relevant
                            combinedTotal = pumpSingleTotal;
                        } else if (rankingType === "double") {
                            //shouldn't be relevant
                            combinedTotal = pumpDoubleTotal;
                        }                        
                    }

                    return <div>{combinedTotal}</div>;
                },                
                
                sortType: "number",
                width: 85,
                disableSortBy: true,                
                show:
                    rankingGame === "" && rankingType === ""
            },          
            {
                Header: isMobile() ? "Best" : "Best",
                accessor: "registration_best_itg",
                Filter: "",
                filter: "between",    
                sortType: "number",
                width: 85,
                disableSortBy: true,
                show:
                    rankingGame === "itg" && rankingType === ""
            },  
            {
                Header: isMobile() ? "Best" : "Best",
                accessor: "registration_best_pump",
                Filter: "",
                filter: "between",    
                sortType: "number",
                width: 85,
                disableSortBy: true,
                show:
                    rankingGame === "pump" && rankingType === ""
            },
            {
                Header: isMobile() ? "Best" : "Best",
                accessor: "registration_best_singles",
                Filter: "",
                filter: "between",    
                sortType: "number",
                width: 85,
                disableSortBy: true,
                show:
                    rankingGame === "" && rankingType === "single"
            },
            {
                Header: isMobile() ? "Best" : "Best",
                accessor: "registration_best_doubles",
                Filter: "",
                filter: "between",    
                sortType: "number",
                width: 85,
                disableSortBy: true,
                show:
                    rankingGame === "" && rankingType === "double"
            },            
            {
                Header: isMobile() ? "Best" : "Best",
                accessor: "registration_best_total",
                Filter: "",
                filter: "between",    
                //  maybe this will be useful later but unsortable so not useful now
                Cell: (row) => {
                    const rowCells = row?.row?.allCells;
                    
                    const itgSingleTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_itg_singles_total"))[0]?.value);
                    const itgDoubleTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_itg_doubles_total"))[0]?.value);
                    const pumpSingleTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_pump_singles_total"))[0]?.value);
                    const pumpDoubleTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_pump_doubles_total"))[0]?.value);

                    const dbBestTotal = parseInt(rowCells.filter((cell)=>(cell?.column?.id === "registration_best_total"))[0]?.value);

                    let bestTotal = 0;

                    if (rankingGame === "") {
                        if (rankingType === "") {
                            //bestTotal = Math.max(itgSingleTotal, itgDoubleTotal, pumpSingleTotal, pumpDoubleTotal);
                            bestTotal = dbBestTotal;
                        } else if (rankingType === "single") {
                            bestTotal = Math.max(itgSingleTotal, pumpSingleTotal);
                        } else if (rankingType === "double") {
                            bestTotal = Math.max(itgDoubleTotal, pumpDoubleTotal);
                        }
                    } else if (rankingGame === "itg") {
                        if (rankingType === "") {
                            bestTotal = Math.max(itgSingleTotal, itgDoubleTotal);
                        } else if (rankingType === "single") {
                            //shouldn't be relevant
                            bestTotal = itgSingleTotal;
                        } else if (rankingType === "double") {
                            //shouldn't be relevant
                            bestTotal = itgDoubleTotal;
                        }
                    } else if (rankingGame === "pump") {
                        if (rankingType === "") {
                            bestTotal = Math.max(pumpSingleTotal, pumpDoubleTotal);
                        } else if (rankingType === "single") {
                            //shouldn't be relevant
                            bestTotal = pumpSingleTotal;
                        } else if (rankingType === "double") {
                            //shouldn't be relevant
                            bestTotal = pumpDoubleTotal;
                        }                        
                    }

                    return <div>{bestTotal}</div>;
                },                                 
                sortType: "number",
                width: 85,
                disableSortBy: true,
                show:
                    rankingGame === "" && rankingType === ""
            },         
            */ 
        ]
        , [rankingDivision, rankingGame, rankingType, isMobile()]
    );

    const extraRankingData = (data, additionalData) => {
/*        
        const itgSingleTpxCharts = data?.registration_itg_singles_tpx_charts
            ? JSON.parse(data.registration_itg_singles_tpx_charts)
            : false;
*/

        const PlayerDisplay = ({playerName, playerImg}) => {
            return (
                (playerName && playerName?.length > 0) && (
                    <Flex flexBasis="200px" mb={4} alignItems="flex-end">
                        { (playerImg && playerImg?.length > 0) ? (
                            <Image flex="1" borderRadius={5} ml="auto" mr="auto" width="50px" src={playerImg} />
                        ) : (<Box width="50px"></Box>)}                    
                        <Heading flex="20" size="md" mb={3} ml={3}>{playerName}</Heading>
                    </Flex>          
                )
            );
        }

        return (
            <>
                <Box width="100%" marginBottom={3}>
                    <Flex wrap="wrap" alignItems="start" justifyContent="space-evenly">
                        <Heading width="100%" size="md">{data?.teams_name}</Heading>
                        <Heading width="100%" size="sm"><span style={{textTransform: "capitalize"}}>{data?.teams_division}</span> Division, <span style={{textTransform: "uppercase"}}>{data?.teams_game}</span> <span style={{textTransform: "capitalize"}}>{data?.teams_type}</span></Heading>
                    </Flex>
                    <Divider mt={2} mb={2}/>
                    <Flex wrap="wrap" alignItems="start" justifyContent="space-evenly">
                        <Flex wrap="wrap" direction="row">
                            <Heading size="md">Members:</Heading>
                            <Divider mt={2} mb={2}/>
                            <PlayerDisplay playerName={data?.CAPTAIN_NAME} playerImg={data?.CAPTAIN_IMG}/>
                            <PlayerDisplay playerName={data?.MEMBER_2_NAME} playerImg={data?.MEMBER_2_IMG}/>
                            <PlayerDisplay playerName={data?.MEMBER_3_NAME} playerImg={data?.MEMBER_3_IMG}/>                  
                        </Flex>
                        <Flex wrap="wrap" direction="column">
                            <Heading size="md">{eventConfig["liveLong"]} Points:</Heading>
                            <Divider mt={2} mb={2}/>                           
                            <Table ml="auto" mr="auto" width={!isMobile() ? "500px" : "200px"} size="sm">
                                <Tbody>
                                    <Tr>
                                        <Th textAlign="center">ECS {eventConfig["eventNum"]} Points</Th>
                                        <Td textAlign="center">{data?.TEAM_ECS_POINTS}</Td>                                
                                    </Tr>
                                    <Tr>
                                        <Th textAlign="center">Stamina Trivia Points</Th>
                                        <Td textAlign="center">{data?.team_trivia_points}</Td>                                
                                    </Tr>
                                    <Tr>
                                        <Th textAlign="center">Sticker Hunt Points</Th>
                                        <Td textAlign="center">{data?.team_sticker_points}</Td>                                
                                    </Tr>
                                    <Tr>
                                        <Th textAlign="center">Silk Song Challenge Points</Th>
                                        <Td textAlign="center">{data?.team_speedrun_points}</Td>                                
                                    </Tr>
                                    <Tr>
                                        <Th textAlign="center">Archi's Challenge Points</Th>
                                        <Td textAlign="center">{data?.team_archi_points}</Td>                                
                                    </Tr>                                                             
                                    <Tr>
                                        <Th textAlign="center">Xiwangz's Challenge Points</Th>
                                        <Td textAlign="center">{data?.team_xiwangz_points}</Td>                                
                                    </Tr>                                                                               
                                </Tbody>
                            </Table>   
                            <Divider mt={2} mb={2}/>                             
                            <Heading size="sm" textAlign="right">Total: {data?.TEAM_SJ_TOTAL_POINTS}</Heading>                               
                        </Flex>
                    </Flex>

                </Box>
            </>
        );
    }

    useEffect(() => {
        let intervalId;
        if(rankingData?.length > 0 && !eventConfig?.["eventOver"]) {
            intervalId = setInterval(rankingDataRequest, refreshTime);
        }
        // Clean up the interval when the component unmounts
        return () => clearInterval(intervalId);        
    }, [rankingData, refreshTime, eventConfig?.["eventOver"]]);

    useEffect(()=> {
        rankingDataRequest();
    }, []);

    try {
        return (
            <>
                <Card mb={4} padding={4} maxWidth={"475px"}>
                    <Heading size="md" mb={3}>Filters</Heading>       
                    <Flex
                        flexFlow="row"
                        flexWrap="wrap"
                        alignSelf="center"
                        gap="15px"
                    >               
                            <FormControl width="unset" variant="floatingCard">
                                <Select                                
                                    minWidth="135px"
                                    value={rankingDivision}
                                    cursor="pointer"
                                    onChange={(filter)=>{
                                        setRankingDivision(filter.target.value)
                                    }}
                                >
                                    <option value="">All</option>
                                    <option value="lower">Lower</option>
                                    <option value="upper">Upper</option>                                                         
                                </Select>                                    
                                <FormLabel>Division</FormLabel>        
                            </FormControl>                     
                            <FormControl width="unset" variant="floatingCard">
                                <Select                                
                                    minWidth="135px"
                                    value={rankingGame}
                                    cursor="pointer"
                                    onChange={(filter)=>{
                                        setRankingGame(filter.target.value)
                                    }}
                                >
                                    <option value="">All</option>
                                    <option value="itg">ITG</option>
                                    <option value="pump">Pump</option>                                                         
                                </Select>                                    
                                <FormLabel>Game</FormLabel>        
                            </FormControl> 
                            <FormControl width="unset" variant="floatingCard">
                                <Select       
                                    width="unset"
                                    minWidth="135px"     
                                    value={rankingType}
                                    cursor="pointer"
                                    onChange={(filter)=>{
                                        setRankingType(filter.target.value)
                                    }}
                                >
                                    <option value="">All</option>
                                    <option value="single">Single</option>
                                    <option value="double">Double</option>
                                </Select>    
                                <FormLabel>Type</FormLabel>  
                            </FormControl>                                                                 
                    </Flex>                    
                </Card>
                <Box width="100%">
                    {
                        (rankingData && rankingData?.length > 0)
                            ?
                                <DataTable data={rankingData} columns={columns} initialState={{...initialState}} customFilters={{...rankingFilter}} customTableSort={[...rankingTableSort]} subRowContents={extraRankingData} additionalData={chartData} searchEnabled={false}/>    
                            :   rankingData !== false
                                ?
                                    <Heading size="md">No submissions yet!</Heading>
                                :
                                    <Heading size="md">Please wait...</Heading>
                    }
                </Box>
            </>
        );
    } catch (e) {
        console.error("Ranking render error: ", e);
    }
}